import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import { breakpoints } from '@avangard/ui/utils';
import { TextField, TextFieldProps } from '@avangard/ui/core';

import { FormStyled } from '@modules/layout/styled';

import type { UpdateSchoolFormValues } from '@modules/schools/requests';

const FormRowTwoChild = styled(FormStyled.FormRow)`
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;

    > * {
        &:not(:last-of-type) {
            margin-inline-end: 24px;
        }

        ${breakpoints.down('xs')} {
            &:not(:last-of-type) {
                margin-inline-end: 0;
            }
        }
    }
`;

const FormColumnInRowTwoChild = styled(FormStyled.FormColumn)`
    max-width: calc(50% - 12px);
    flex-wrap: wrap;
    align-items: flex-start;

    &:not:last-child {
        margin-inline-end: 24px;
    }

    ${breakpoints.down('xs')} {
        max-width: 100%;

        &:not:last-child {
            margin-inline-end: 0;
            margin-block-end: 24px;
        }
    }
`;

const CompanyResponsiblesFormGroup = React.memo((): React.ReactElement => {
    const { t } = useTranslation(['common', 'errors']);

    const { values, errors, handleChange, handleBlur } = useFormikContext<UpdateSchoolFormValues>();

    const commonProps = React.useMemo(
        (): Partial<TextFieldProps> => ({
            fullWidth: true,
            required: true,
            onChange: handleChange,
            onBlur: handleBlur,
            inputProps: {
                // Вырубаем дефолтную валидацию input required, в самом TextField оставляем, т.к. нужна
                // красная звездочка в ui (валидация будет через formik)
                required: false,
            },
        }),
        [handleChange, handleBlur],
    );

    return (
        <FormRowTwoChild>
            <FormColumnInRowTwoChild>
                <FormStyled.FormColumnTitle>
                    {t('common:form.labels.requisites.titles.responsible')}
                </FormStyled.FormColumnTitle>

                <TextField
                    {...commonProps}
                    id='requisites.responsibleName'
                    label={t('common:form.labels.requisites.responsible_name')}
                    placeholder={t('common:form.placeholder.requisites.responsible_name') ?? ''}
                    value={values.requisites?.responsibleName}
                    error={!!errors.requisites?.responsibleName}
                    helperText={errors.requisites?.responsibleName}
                />

                <TextField
                    {...commonProps}
                    id='requisites.responsiblePhone'
                    label={t('common:form.labels.requisites.responsible_phone')}
                    placeholder={t('common:form.placeholder.requisites.responsible_phone') ?? ''}
                    value={values.requisites?.responsiblePhone}
                    error={!!errors.requisites?.responsiblePhone}
                    helperText={errors.requisites?.responsiblePhone}
                />

                <TextField
                    {...commonProps}
                    id='requisites.responsibleEmail'
                    label={t('common:form.labels.requisites.responsible_email')}
                    placeholder={t('common:form.placeholder.requisites.responsible_email') ?? ''}
                    value={values.requisites?.responsibleEmail}
                    error={!!errors.requisites?.responsibleEmail}
                    helperText={errors.requisites?.responsibleEmail}
                />
            </FormColumnInRowTwoChild>

            <FormColumnInRowTwoChild>
                <FormStyled.FormColumnTitle>
                    {t('common:form.labels.requisites.titles.signer')}
                </FormStyled.FormColumnTitle>

                <TextField
                    {...commonProps}
                    id='requisites.signerName'
                    label={t('common:form.labels.requisites.signer_name')}
                    placeholder={t('common:form.placeholder.requisites.signer_name') ?? ''}
                    value={values.requisites?.signerName}
                    error={!!errors.requisites?.signerName}
                    helperText={errors.requisites?.signerName}
                />

                <TextField
                    {...commonProps}
                    id='requisites.signerPosition'
                    label={t('common:form.labels.requisites.signer_position')}
                    placeholder={t('common:form.placeholder.requisites.signer_position') ?? ''}
                    value={values.requisites?.signerPosition}
                    error={!!errors.requisites?.signerPosition}
                    helperText={errors.requisites?.signerPosition}
                />
            </FormColumnInRowTwoChild>
        </FormRowTwoChild>
    );
});

export { CompanyResponsiblesFormGroup };
